import React, { useState, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "react-query";
import {
  Alert,
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  InputAdornment as MuiInputAdornment,
  useMediaQuery,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  Stack,
  InputAdornment,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import LockIcon from "@mui/icons-material/Lock";
import SearchIcon from "@mui/icons-material/Search";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HistoryIcon from "@mui/icons-material/History";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import client from "../../../../API";
import AdminPayoutResolver from "./adminpayoutmerger";

// ─────────────────────────────────────────────────────────────────────────────
// ZeroBalanceDialog — password-gated balance wipe
// ─────────────────────────────────────────────────────────────────────────────

const ZeroBalanceDialog = ({ open, onClose, user, onSuccess }) => {
  const [step, setStep] = useState("confirm");   // "confirm" | "password"
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset on open
  useEffect(() => {
    if (open) { setStep("confirm"); setPassword(""); setReason(""); setError(null); }
  }, [open]);

  const currentBalance = parseFloat(user?.balance || 0);
  const fmt = (v) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v || 0);

  const handleSubmit = async () => {
    if (!password.trim()) { setError("Password is required"); return; }
    setLoading(true);
    setError(null);
    try {
      const result = await client.withdrawals.zeroUserBalance(user.id, {
        password,
        reason: reason.trim() || undefined,
      });
      onSuccess(result);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to zero balance");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      {step === "confirm" ? (
        <>
          <DialogTitle sx={{ color: "error.main", fontWeight: 700, display: "flex", gap: 1, alignItems: "center" }}>
            <DeleteForeverIcon /> Zero Balance Override
          </DialogTitle>
          <DialogContent dividers>
            <DialogContentText component="div">
              <Stack spacing={2}>
                <Alert severity="error">
                  This will <strong>permanently remove</strong> the entire balance for{" "}
                  <strong>{user?.username}</strong>. The ledger will record this action
                  under your admin account. It cannot be automatically reversed.
                </Alert>
                <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">Current balance</Typography>
                  <Typography variant="h5" fontWeight={700} color="error.main">
                    {fmt(currentBalance)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Will become <strong>$0.00</strong>
                  </Typography>
                </Box>
                <TextField
                  label="Reason (optional but recommended)"
                  size="small" fullWidth multiline minRows={2}
                  value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Duplicate account — balance transferred manually"
                />
              </Stack>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained" color="error"
              disabled={currentBalance === 0}
              onClick={() => setStep("password")}
              startIcon={<LockIcon />}
            >
              Continue → Enter Password
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle sx={{ color: "error.main", fontWeight: 700, display: "flex", gap: 1, alignItems: "center" }}>
            <LockIcon /> Confirm with Your Password
          </DialogTitle>
          <DialogContent dividers>
            <DialogContentText component="div">
              <Stack spacing={2}>
                <Typography variant="body2">
                  Enter <strong>your admin login password</strong> to authorise zeroing{" "}
                  <strong>{user?.username}</strong>'s balance of{" "}
                  <strong style={{ color: "red" }}>{fmt(currentBalance)}</strong>.
                </Typography>
                <TextField
                  label="Your admin password"
                  size="small" fullWidth autoFocus
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(null); }}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                  error={!!error}
                  helperText={error}
                  InputProps={{
                    endAdornment: (
                      <MuiInputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword((v) => !v)}>
                          {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </MuiInputAdornment>
                    ),
                  }}
                />
                {reason && (
                  <Typography variant="caption" color="text.secondary">
                    Reason logged: "{reason}"
                  </Typography>
                )}
              </Stack>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStep("confirm")} disabled={loading}>Back</Button>
            <Button
              variant="contained" color="error" disabled={loading || !password.trim()}
              onClick={handleSubmit}
              startIcon={loading ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
            >
              {loading ? "Processing…" : "Zero Balance"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared helpers
// ─────────────────────────────────────────────────────────────────────────────

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val || 0);

const formatDateTime = (val) => {
  if (!val) return "—";
  return new Date(val).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Tab panel wrapper
// ─────────────────────────────────────────────────────────────────────────────

const TabPanel = ({ children, value, index }) =>
  value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;

// ─────────────────────────────────────────────────────────────────────────────
// Summary stat card
// ─────────────────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, color = "text.primary" }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" noWrap>
      {label}
    </Typography>
    <Typography variant="h5" fontWeight="bold" color={color}>
      {value}
    </Typography>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// USER BALANCES TAB
// ─────────────────────────────────────────────────────────────────────────────

const UserBalancesTab = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [accountTypeFilter, setAccountTypeFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [ledgerPage, setLedgerPage] = useState(0);
  const [ledgerRowsPerPage, setLedgerRowsPerPage] = useState(50);
  const [zeroDialogOpen, setZeroDialogOpen] = useState(false);
  const [zeroSuccessBanner, setZeroSuccessBanner] = useState(null);

  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleZeroSuccess = (result) => {
    // Invalidate both queries so the UI refreshes immediately
    queryClient.invalidateQueries(["adminUserBalanceLedger", selectedUser?.id]);
    queryClient.invalidateQueries(["adminUserBalances"]);
    // Update selectedUser balance locally so the dialog header reflects $0 right away
    setSelectedUser((u) => u ? { ...u, balance: 0 } : u);
    setZeroSuccessBanner(result);
  };

  const { data: balancesData, isLoading } = useQuery(
    ["adminUserBalances", page, rowsPerPage, debouncedSearch, accountTypeFilter],
    () =>
      client.withdrawals.getUserBalances({
        page,
        rows: rowsPerPage,
        search: debouncedSearch || undefined,
        account_type: accountTypeFilter || undefined,
      }),
    { keepPreviousData: true }
  );

  const users = balancesData?.data || [];
  const totalUsers = balancesData?.total || 0;
  const totalBalance = balancesData?.total_balance || 0;

  const { data: ledgerData, isLoading: ledgerLoading } = useQuery(
    ["adminUserBalanceLedger", selectedUser?.id, ledgerPage, ledgerRowsPerPage],
    () =>
      client.withdrawals.getUserBalanceLedger(selectedUser.id, {
        page: ledgerPage,
        rows: ledgerRowsPerPage,
      }),
    { enabled: !!selectedUser, keepPreviousData: true }
  );

  const ledgerEntries = ledgerData?.ledger || [];
  const ledgerTotal = ledgerData?.total || 0;

  const handleOpenLedger = useCallback((user) => {
    setSelectedUser(user);
    setLedgerPage(0);
  }, []);

  const handleCloseLedger = useCallback(() => {
    setSelectedUser(null);
    setLedgerPage(0);
  }, []);

  return (
    <>
      <Paper sx={{ p: 2, mb: 2, display: "flex", gap: 4, flexWrap: "wrap" }}>
        <StatCard label="Total Balance (All Users)" value={formatCurrency(totalBalance)} />
        <StatCard label="Total Users" value={totalUsers.toLocaleString()} />
      </Paper>

      <Paper sx={{ p: 2, mb: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          label="Search users"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Username, email, or ID"
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Account Type</InputLabel>
          <Select
            value={accountTypeFilter}
            label="Account Type"
            onChange={(e) => {
              setAccountTypeFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="creator">Creator</MenuItem>
            <MenuItem value="business">Business</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {users.map((user) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={user.id}>
              <Card variant="outlined">
                <CardActionArea onClick={() => handleOpenLedger(user)}>
                  <CardContent>
                    <Typography variant="subtitle2" noWrap>
                      {user.username}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {user.email || "No email"}
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold">
                        {formatCurrency(user.balance)}
                      </Typography>
                      <Chip
                        label={user.account_type === "creator" ? "Creator" : "Business"}
                        color={user.account_type === "creator" ? "secondary" : "primary"}
                        size="small"
                      />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
          {users.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No users found
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

      <TablePagination
        component="div"
        count={totalUsers}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100]}
      />

      {/* Per-user ledger dialog */}
      <Dialog
        open={!!selectedUser}
        onClose={handleCloseLedger}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Box>
            <Typography variant="h6">
              {selectedUser?.username} — Balance Ledger
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedUser?.email} | Current Balance:{" "}
              {formatCurrency(ledgerData?.user?.balance)}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseLedger}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {ledgerLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Operation</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Balance After</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ledgerEntries.map((entry) => {
                      const isCredit = parseFloat(entry.change_amount) > 0;
                      return (
                        <TableRow key={entry.id}>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {formatDateTime(entry.created_at)}
                          </TableCell>
                          <TableCell>{entry.operation_type}</TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: isCredit ? "success.main" : "error.main",
                              fontWeight: "bold",
                            }}
                          >
                            {isCredit ? "+" : ""}
                            {formatCurrency(parseFloat(entry.change_amount))}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(parseFloat(entry.new_balance))}
                          </TableCell>
                          <TableCell>{entry.reason || "—"}</TableCell>
                        </TableRow>
                      );
                    })}
                    {ledgerEntries.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No ledger entries found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={ledgerTotal}
                page={ledgerPage}
                onPageChange={(_, newPage) => setLedgerPage(newPage)}
                rowsPerPage={ledgerRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setLedgerRowsPerPage(parseInt(e.target.value, 10));
                  setLedgerPage(0);
                }}
                rowsPerPageOptions={[25, 50, 100]}
              />

              {/* ── Danger Zone ──────────────────────────────────────────── */}
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "error.light",
                  borderRadius: 1,
                  p: 2,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255,0,0,0.05)" : "error.50",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="error.main"
                  fontWeight={700}
                  gutterBottom
                >
                  Danger Zone
                </Typography>

                {zeroSuccessBanner && (
                  <Alert
                    severity="success"
                    sx={{ mb: 1.5 }}
                    onClose={() => setZeroSuccessBanner(null)}
                  >
                    Balance cleared — previous:{" "}
                    {formatCurrency(parseFloat(zeroSuccessBanner.previous_balance))}, now{" "}
                    <strong>$0.00</strong>
                  </Alert>
                )}

                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Remove the entire balance for <strong>{selectedUser?.username}</strong>.
                      A full ledger entry is written before clearing. Requires your admin
                      password to confirm.
                    </Typography>
                  </Box>
                  <Tooltip
                    title={
                      parseFloat(selectedUser?.balance || ledgerData?.user?.balance || 0) === 0
                        ? "Balance is already $0"
                        : "Zero out this user's balance"
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteForeverIcon />}
                        disabled={
                          parseFloat(selectedUser?.balance ?? ledgerData?.user?.balance ?? 0) === 0
                        }
                        onClick={() => setZeroDialogOpen(true)}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Zero Balance
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Zero-balance confirmation dialog ─────────────────────────────── */}
      {selectedUser && (
        <ZeroBalanceDialog
          open={zeroDialogOpen}
          onClose={() => setZeroDialogOpen(false)}
          user={selectedUser?.balance !== undefined
            ? selectedUser
            : { ...selectedUser, balance: ledgerData?.user?.balance || 0 }}
          onSuccess={handleZeroSuccess}
        />
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// T2 HISTORY TAB
// ─────────────────────────────────────────────────────────────────────────────

const T2HistoryTab = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [operationTypeFilter, setOperationTypeFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [orderBy, setOrderBy] = useState("created_at");
  const [orderDir, setOrderDir] = useState("desc");
  const [selectedEntry, setSelectedEntry] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading } = useQuery(
    [
      "adminT2History",
      page,
      rowsPerPage,
      debouncedSearch,
      operationTypeFilter,
      startDate,
      endDate,
      orderBy,
      orderDir,
    ],
    () =>
      client.withdrawals.getT2History({
        page,
        rows: rowsPerPage,
        search: debouncedSearch || undefined,
        operation_type: operationTypeFilter || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        order_by: orderBy,
        order_dir: orderDir,
      }),
    { keepPreviousData: true }
  );

  const entries = data?.data || [];
  const totalEntries = data?.total || 0;
  const totalCredits = data?.total_credits || 0;
  const totalDebits = data?.total_debits || 0;
  const netFlow = data?.net_flow ?? totalCredits - totalDebits;

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(column);
      setOrderDir("desc");
    }
    setPage(0);
  };

  const SortableCell = ({ column, label, align = "left" }) => (
    <TableCell align={align} sortDirection={orderBy === column ? orderDir : false}>
      <TableSortLabel
        active={orderBy === column}
        direction={orderBy === column ? orderDir : "asc"}
        onClick={() => handleSort(column)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const OPERATION_TYPES = [
    "credit", "debit", "payout", "withdrawal",
    "refund", "adjustment", "transfer", "fee",
  ];

  return (
    <>
      <Paper sx={{ p: 2, mb: 2, display: "flex", gap: 4, flexWrap: "wrap" }}>
        <StatCard label="Total Entries" value={totalEntries.toLocaleString()} />
        <StatCard label="Total Credits" value={formatCurrency(totalCredits)} color="success.main" />
        <StatCard label="Total Debits" value={formatCurrency(totalDebits)} color="error.main" />
        <StatCard
          label="Net Flow"
          value={formatCurrency(netFlow)}
          color={netFlow >= 0 ? "success.main" : "error.main"}
        />
      </Paper>

      <Paper sx={{ p: 2, mb: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          label="Search"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="User, ID, or reason"
          sx={{ minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Operation Type</InputLabel>
          <Select
            value={operationTypeFilter}
            label="Operation Type"
            onChange={(e) => { setOperationTypeFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All Types</MenuItem>
            {OPERATION_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Start Date" type="date" size="small" value={startDate}
          onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
        />
        <TextField
          label="End Date" type="date" size="small" value={endDate}
          onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
        />
      </Paper>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <SortableCell column="created_at" label="Date" />
                  <TableCell>User</TableCell>
                  <TableCell>Account Type</TableCell>
                  <SortableCell column="operation_type" label="Operation" />
                  <SortableCell column="change_amount" label="Amount" align="right" />
                  <SortableCell column="new_balance" label="Balance After" align="right" />
                  <TableCell>Reason / Reference</TableCell>
                  <TableCell>ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => {
                  const isCredit = parseFloat(entry.change_amount) > 0;
                  return (
                    <TableRow
                      key={entry.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => setSelectedEntry(entry)}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDateTime(entry.created_at)}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={entry.user_email || ""} placement="top" arrow>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                            {entry.username || entry.user_id || "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {entry.account_type ? (
                          <Chip
                            label={entry.account_type}
                            color={entry.account_type === "creator" ? "secondary" : "primary"}
                            size="small" variant="outlined"
                          />
                        ) : "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.operation_type || "unknown"}
                          size="small" variant="filled"
                          color={isCredit ? "success" : entry.operation_type === "fee" ? "warning" : "error"}
                          sx={{ fontWeight: 600, textTransform: "capitalize" }}
                        />
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: isCredit ? "success.main" : "error.main", fontWeight: "bold", whiteSpace: "nowrap" }}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                          {isCredit ? <TrendingUpIcon fontSize="inherit" /> : <TrendingDownIcon fontSize="inherit" />}
                          <span>{isCredit ? "+" : ""}{formatCurrency(parseFloat(entry.change_amount))}</span>
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {formatCurrency(parseFloat(entry.new_balance))}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {entry.reason || entry.reference_id || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.disabled" sx={{ fontFamily: "monospace" }}>
                          {entry.id}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No transactions found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div" count={totalEntries} page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 250]}
          />
        </Paper>
      )}

      <Dialog open={!!selectedEntry} onClose={() => setSelectedEntry(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Transaction Detail</Typography>
          <IconButton onClick={() => setSelectedEntry(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEntry && (
            <Table size="small">
              <TableBody>
                {[
                  ["ID", selectedEntry.id],
                  ["Date", formatDateTime(selectedEntry.created_at)],
                  ["User", selectedEntry.username || selectedEntry.user_id],
                  ["Email", selectedEntry.user_email || "—"],
                  ["Account Type", selectedEntry.account_type || "—"],
                  ["Operation", selectedEntry.operation_type],
                  ["Amount",
                    (parseFloat(selectedEntry.change_amount) > 0 ? "+" : "") +
                    formatCurrency(parseFloat(selectedEntry.change_amount))],
                  ["Balance Before",
                    formatCurrency(parseFloat(
                      selectedEntry.previous_balance ??
                      selectedEntry.new_balance - selectedEntry.change_amount
                    ))],
                  ["Balance After", formatCurrency(parseFloat(selectedEntry.new_balance))],
                  ["Reason", selectedEntry.reason || "—"],
                  ["Reference ID", selectedEntry.reference_id || "—"],
                  ["Source", selectedEntry.source || "—"],
                ].map(([label, value]) => (
                  <TableRow key={label}>
                    <TableCell sx={{ fontWeight: 600, width: "40%", color: "text.secondary" }}>
                      {label}
                    </TableCell>
                    <TableCell>{value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ROOT COMPONENT — 3 tabs
// ─────────────────────────────────────────────────────────────────────────────

const UserBalancesView = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box>
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newVal) => setActiveTab(newVal)}
          sx={{ px: 2 }}
          TabIndicatorProps={{ sx: { height: 3, borderRadius: 1.5 } }}
        >
          <Tab
            icon={<AccountBalanceWalletIcon fontSize="small" />}
            iconPosition="start"
            label="User Balances"
            sx={{ textTransform: "none", fontWeight: 600, gap: 0.5 }}
          />
          <Tab
            icon={<HistoryIcon fontSize="small" />}
            iconPosition="start"
            label="T2 History"
            sx={{ textTransform: "none", fontWeight: 600, gap: 0.5 }}
          />
          {/* ← NEW: Payout Attribution tab */}
          <Tab
            icon={<SwapHorizIcon fontSize="small" />}
            iconPosition="start"
            label="Payout Attribution"
            sx={{ textTransform: "none", fontWeight: 600, gap: 0.5 }}
          />
        </Tabs>
        <Divider />
      </Paper>

      <TabPanel value={activeTab} index={0}>
        <UserBalancesTab />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <T2HistoryTab />
      </TabPanel>

      {/* ← NEW: Lazy-mount so the resolver only loads when the tab is active */}
      <TabPanel value={activeTab} index={2}>
        <AdminPayoutResolver />
      </TabPanel>
    </Box>
  );
};

export default UserBalancesView;